import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { PerformanceAnalysisAdapter } from '../../adapters/performance-analysis.adapter';
import { AppState } from '../../app.reducer';
import {
  exportPerformanceAnalysisCsv,
  exportPerformanceAnalysisCsvFailure,
  exportPerformanceAnalysisCsvSuccess,
  exportPerformanceAnalysisPdf,
  exportPerformanceAnalysisPdfFailure,
  exportPerformanceAnalysisPdfSuccess,
  loadPerformanceAnalysis,
  loadPerformanceAnalysisFailure,
  loadPerformanceAnalysisFilters,
  loadPerformanceAnalysisFiltersFailure,
  loadPerformanceAnalysisFiltersSuccess,
  loadPerformanceAnalysisSuccess,
} from './performance-analysis.actions';
import { selectPerformanceAnalysisQuery } from './performance-analysis.reducer';

@Injectable()
export class PerformanceAnalysisEffects {
  private readonly actions$ = inject(Actions);
  private readonly adapter = inject(PerformanceAnalysisAdapter);
  private readonly store = inject(Store<AppState>);

  loadFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPerformanceAnalysisFilters),
      exhaustMap(() =>
        this.adapter.getFilters().pipe(
          map((filters) => loadPerformanceAnalysisFiltersSuccess({ filters })),
          catchError((error) => of(loadPerformanceAnalysisFiltersFailure({ error })))
        )
      )
    )
  );

  loadPerformanceAnalysis$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPerformanceAnalysis),
      switchMap(({ query }) =>
        this.adapter.getPerformanceAnalysis(query).pipe(
          map((data) => loadPerformanceAnalysisSuccess({ data })),
          catchError((error) => of(loadPerformanceAnalysisFailure({ error })))
        )
      )
    )
  );

  exportCsv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(exportPerformanceAnalysisCsv),
      withLatestFrom(this.store.select(selectPerformanceAnalysisQuery)),
      exhaustMap(([, query]) =>
        this.adapter.exportCsv(query).pipe(
          map((response) => exportPerformanceAnalysisCsvSuccess({ csv: response.csv })),
          catchError((error) => of(exportPerformanceAnalysisCsvFailure({ error })))
        )
      )
    )
  );

  downloadCsv$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(exportPerformanceAnalysisCsvSuccess),
        tap(({ csv }) => {
          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'performance-analyse.csv';
          link.click();
          URL.revokeObjectURL(url);
        })
      ),
    { dispatch: false }
  );

  exportPdf$ = createEffect(() =>
    this.actions$.pipe(
      ofType(exportPerformanceAnalysisPdf),
      withLatestFrom(this.store.select(selectPerformanceAnalysisQuery)),
      exhaustMap(([, query]) =>
        this.adapter.exportPdf(query).pipe(
          map((blob) => exportPerformanceAnalysisPdfSuccess({ blob })),
          catchError((error) => of(exportPerformanceAnalysisPdfFailure({ error })))
        )
      )
    )
  );

  downloadPdf$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(exportPerformanceAnalysisPdfSuccess),
        tap(({ blob }) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'performance-analyse.pdf';
          link.click();
          URL.revokeObjectURL(url);
        })
      ),
    { dispatch: false }
  );
}
